// External Services
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { requestForToken } from '../firebase';
import { patchRequest } from '../services/api/base';
import { StoreUtils } from './util';

export const clickToPrint = async (fileUrl: string) => {
  const newIframe = document.createElement('iframe');
  newIframe.width = '0';
  newIframe.height = '0';
  document.body.appendChild(newIframe);
  fetch(fileUrl)
    .then((response) => response.blob())
    .then((myBlob: Blob) => {
      const objectURL = URL.createObjectURL(myBlob);
      newIframe.src = '';
      newIframe.src = objectURL;
      URL.revokeObjectURL(objectURL);
    })
    .then(() => {
      window.setTimeout(function () {
        newIframe?.contentWindow?.focus();
        newIframe?.contentWindow?.print();
      }, 200);
    });
};

export const htmlToPrint = async (fileUrl: string) => {
  const printWindow = window.open(fileUrl, '_blank', 'height=400,width=600');
  window.onfocus = () => {
    // Close the print window when focus is returned to the parent page
    printWindow?.close();
  };
};

export const clickToDownload = async (fileUrl: string) => {
  const link = document.createElement('a');
  link.href = fileUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const clickToDownloadXML = async (fileUrl: string) => {
  try {
    const response = await fetch(fileUrl);
    const responseBlob = await response.blob();
    // Create a temporary URL for the blob
    const blobUrl = window.URL.createObjectURL(new Blob([responseBlob]));

    // Create an anchor element
    const link = document.createElement('a');
    link.href = blobUrl;

    const fileName = fileUrl.split('/').pop();

    // Set the download attribute to specify the file name
    link.download = fileName || '1Pharmacy';

    // Programmatically click the link to trigger the download
    link.click();

    // Clean up: revoke the temporary URL
    window.URL.revokeObjectURL(blobUrl);
  } catch (e) {
    console.log(e);
  }
};

export const downloadJsonFile = async (fileUrl: string, fileName?: string) => {
  try {
    const response = await fetch(fileUrl);
    const responseBlob = await response.blob();
    const blobUrl = window.URL.createObjectURL(new Blob([responseBlob]));

    const link = document.createElement('a');
    link.href = blobUrl;

    link.download = (fileName ? fileName : '1Pharmacy') + '.json';

    link.click();

    window.URL.revokeObjectURL(blobUrl);
  } catch (e) {
    console.log(e);
  }
};

// Amplitude
export const initAmplitude = () => {
  try {
    const { session: activeSession } = StoreUtils.getActiveSession();

    const amplitudeAPIKey = process.env.REACT_APP_AMPLITUDE_API_KEY;

    if (process.env.REACT_APP_TYPE === 'prod' && amplitudeAPIKey) {
      amplitude.add(sessionReplayPlugin());
      amplitude.init(amplitudeAPIKey, { autocapture: true });
      console.log('Amplitude Initialized');

      const identifyObj = new amplitude.Identify();

      identifyObj.set('userId', activeSession?.userData?.userId);
      identifyObj.set('name', activeSession?.userData?.name);
      identifyObj.set('company', activeSession?.userData?.company);
      identifyObj.set('cid', activeSession?.cid);

      amplitude.identify(identifyObj);
    }
  } catch (error) {
    console.error('Amplitude Initialization Error', error);
  }
};

/* eslint-disable  @typescript-eslint/no-explicit-any */
export const sendAmplitudeEvent = (eventName: string, data: any) => {
  if (process.env.REACT_APP_TYPE === 'prod') {
    amplitude.track(eventName, data);
  }
};

// Firebase
export const sendFCMTokenToServer = async () => {
  try {
    const userId = StoreUtils.getKeyFromActiveSession(['session-token']);
    if (userId) {
      const token = await requestForToken();
      if (token) {
        await patchRequest(`user/${userId}`, { webFcmToken: token });
        sendAmplitudeEvent('FCM token sent to server', { userId: userId as string, token });
        console.log('FCM token has been sent to server', token);
      }
    }
  } catch (error) {
    console.error('FCM token sent server error', error);
  }
};
